import { useIntl } from 'react-intl';

import { useApolloClient, useQuery } from '@apollo/client';

import {
  WishlistList,
  WishlistListByIdQuery,
  WishlistListByIdQueryVariables,
  WishlistListsQuery,
} from '@notino/shared/definitions/types';

import wishlistModifierMessages from '@components/Universals/WishlistModifier/messages';
import wishlistListsQuery from '@components/Universals/WishlistModifier/queries/wishlistLists.graphql';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';

import wishlistListByIdQuery from '../../WishList/queries/wishlistListById.graphql';

export const useListsData = () => {
  const { formatMessage } = useIntl();
  const { data: listsData } = useQuery<WishlistListsQuery>(wishlistListsQuery);

  const lists = (listsData?.WishlistLists?.lists || []).map((l) =>
    l.id === useWishlistLists.DEFAULT_LIST_ID
      ? { ...l, name: formatMessage(wishlistModifierMessages.defaultListName) }
      : l
  );

  return { lists };
};

export const useListDataCache = () => {
  const cache = useApolloClient().cache;

  const addNewListCache = (list: WishlistList) => {
    const cachedLists = cache.readQuery<WishlistListsQuery>({
      query: wishlistListsQuery,
    }).WishlistLists.lists;

    if (!cachedLists) {
      return;
    }

    cache.writeQuery<WishlistListsQuery>({
      query: wishlistListsQuery,
      data: {
        WishlistLists: {
          lists: [
            ...cache.readQuery<WishlistListsQuery>({
              query: wishlistListsQuery,
            }).WishlistLists.lists,
            list,
          ],
        },
      },
    });
  };

  const renameListCache = (listId: string, name: string) => {
    const cachedLists = cache.readQuery<WishlistListsQuery>({
      query: wishlistListsQuery,
    })?.WishlistLists?.lists;

    if (cachedLists) {
      cache.writeQuery<WishlistListsQuery>({
        query: wishlistListsQuery,
        data: {
          WishlistLists: {
            lists: [
              ...cachedLists.map((l) => (l.id === listId ? { ...l, name } : l)),
            ],
          },
        },
      });
    }

    const cachedList = cache.readQuery<
      WishlistListByIdQuery,
      WishlistListByIdQueryVariables
    >({
      query: wishlistListByIdQuery,
      variables: {
        id: listId,
      },
    })?.WishlistListById;

    if (cachedList) {
      cache.writeQuery<WishlistListByIdQuery, WishlistListByIdQueryVariables>({
        query: wishlistListByIdQuery,
        variables: {
          id: listId,
        },
        data: {
          WishlistListById: {
            ...cachedList,
            name,
          },
        },
      });
    }
  };

  const deleteListCache = (id: string) => {
    const cachedLists = cache.readQuery<WishlistListsQuery>({
      query: wishlistListsQuery,
    })?.WishlistLists?.lists;

    if (cachedLists) {
      cache.writeQuery<WishlistListsQuery>({
        query: wishlistListsQuery,
        data: {
          WishlistLists: {
            lists: cachedLists.filter((l) => l.id !== id),
          },
        },
      });
    }
  };

  return { addNewListCache, renameListCache, deleteListCache };
};
